import { memo, useEffect } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { unwrapResult } from '@reduxjs/toolkit';
import { useAppDispatch, useResponsive } from '../../../../hooks';
import { Dialog, message, VStack } from '../../components-v4';
import { useV4Theme } from '../../components-v4/theme';
import {
  clearMetersData,
  getPublicRequestMeters,
  postMetersReadings,
  setShowMetersReadingsModal,
  setShowMetersResultModal,
} from '../../request-single-page-slice';
import { AutoMeters, ModalHeader, MeterCard, ModalFooter } from './ui';
import {
  getMetersData,
  getPublicId,
  getShowMetersReadingsModalState,
} from '../../selectors';
import { MetersVariants } from '../meters/constants';
import {
  StyledDialogContent,
  StyledDialogPanel,
} from './meters-readings-modal.styled';

export type ReadingsFormValues = Record<string, number>;

export const MetersReadingsModal = memo(() => {
  const dispatch = useAppDispatch();
  const { viewport } = useResponsive();
  const showMetersReadingsModal = useSelector(getShowMetersReadingsModalState);
  const theme = useV4Theme();

  const publicId = useSelector(getPublicId);
  const allMeters = useSelector(getMetersData);
  const nonAutomaticMeters = allMeters.filter((m) => !m.is_automatic);
  const automaticMeters = allMeters.filter((m) => m.is_automatic);

  const methods = useForm<ReadingsFormValues>({
    reValidateMode: 'onSubmit',
  });
  const { handleSubmit, trigger, reset } = methods;

  useEffect(() => {
    if (!showMetersReadingsModal) {
      reset();
    }
  }, [reset, showMetersReadingsModal]);

  const handleClose = () => {
    reset();
    dispatch(setShowMetersReadingsModal(false));
  };

  const onSubmit = async (values: ReadingsFormValues) => {
    await trigger();

    const data = nonAutomaticMeters.map((meter) => ({
      meter_id: meter.id,
      values: MetersVariants[meter.type.type].readings.map(
        (_, index) => values[`${meter.id}_${index}`]
      ),
    }));

    try {
      await dispatch(postMetersReadings({ id: publicId, data })).then(
        unwrapResult
      );
      handleClose();
      dispatch(getPublicRequestMeters(publicId));
      dispatch(setShowMetersResultModal('readings'));
      clearMetersData();
    } catch {
      message({
        type: 'error',
        text: 'Упс...Что-то пошло не так, повторите попытку',
      });
    }
  };

  return (
    <Dialog.Root open={showMetersReadingsModal} onOpenChange={handleClose}>
      <Dialog.Portal>
        <StyledDialogContent
          overlayStyle={{
            ...(!viewport.desktop && { backgroundColor: theme.colors.white }),
          }}
        >
          <StyledDialogPanel>
            <ModalHeader onClose={handleClose} />

            <FormProvider {...methods}>
              <VStack
                padding={`0 ${theme.spacings[viewport.tablet ? 'l' : 'base']}`}
                flex={1}
              >
                <VStack flex={1}>
                  {nonAutomaticMeters.map((meter) => (
                    <MeterCard key={meter.id} {...meter} />
                  ))}
                </VStack>

                <ModalFooter
                  onSend={handleSubmit(onSubmit)}
                  onClose={handleClose}
                />

                {!!automaticMeters.length && (
                  <AutoMeters meters={automaticMeters} />
                )}
              </VStack>
            </FormProvider>
          </StyledDialogPanel>
        </StyledDialogContent>
      </Dialog.Portal>
    </Dialog.Root>
  );
});
